import {
	isRouteErrorResponse,
	Link,
	useLoaderData,
	useNavigate,
	useRouteError,
} from '@remix-run/react'
import {
	type LoaderFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	defer,
	redirect,
} from '@remix-run/node'

import Section from '#app/components/layout/section.tsx'
import Button from '#app/components/buttons/button'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import { TypographyParagraph } from '../components/typography'
import generateCollectionMeta from '#app/utils/meta/collectionMeta.ts'
import {
	DynamicWallet,
	getDomainUrl,
	mapNFTsToCollection,
} from '#app/utils/misc.server.ts'
import CollectionProfile from '#app/components/collectionProfile/collectionProfile.tsx'
import { getDynamicUser } from '#app/utils/dynamic.server.ts'
import { useState } from 'react'
import {
	getAttestationsForCollection,
	getCollectionAttestation,
	getNFTsForCollection,
	getUser,
} from '#app/server/db.server.ts'
import { mapReferenceEntriesToFormData } from '#app/utils/misc.tsx'
import {
	AttestationType,
	MariaDBNFTsForCollectionResponse,
	MetadataProperties,
} from '#types/loreTypes.tsx'

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	matches,
	location,
}) => {
	if (data) {
		const { origin, collection } = data
		const { pathname } = location
		const metaPayload = generateCollectionMeta({
			origin,
			path: pathname,
			collectionAttestation: collection,
		})
		return metaPayload
	} else {
		const parentMeta = matches.flatMap(match => match.meta ?? [])
		return parentMeta
	}
}

export const loader: LoaderFunction = async ({
	request,
	params,
}: LoaderFunctionArgs) => {
	const url = new URL(request.url)
	const origin = getDomainUrl(request)

	let collection: AttestationType | undefined = undefined
	let artist:
		| { username: string; userAvatar?: string; userId?: string }
		| undefined = undefined
	let nfts: MariaDBNFTsForCollectionResponse
	let nftCount: number = 0
	let chains: string[] = []
	let signModalParams: {
		userWallets: { count: number; wallets: DynamicWallet[] } | undefined
		isOpen: boolean
		templateFromURL: string | undefined
		cidMetadata: AttestationType | undefined
		matchingAddress: string | undefined
		isValidSign: boolean
		pendingPinFromURL:
			| { template: string; formData: MetadataProperties }
			| undefined
	} = {
		userWallets: undefined,
		matchingAddress: undefined,
		isValidSign: false,
		templateFromURL: undefined,
		cidMetadata: undefined,
		isOpen: false,
		pendingPinFromURL: undefined,
	}

	if (!params.cid) {
		throw new Error('URL has no cid')
	}

	const collectionLore = getAttestationsForCollection({
		collectionAttestationIPFSCID: params.cid,
		template_slug: [
			'proofOfExhibition',
			'offchainSale',
			'offchainLend',
			'mediaCitation',
			'tweet',
			'cast',
			'media',
			// 'instagram',
			'comment',
			'countersign',
		],
		orderDirection: 'DESC',
	})

	try {
		// @ts-ignore
		nfts = getNFTsForCollection({
			attestationCID: params.cid,
		})
	} catch (e) {
		console.error('ERROR FETCHING NFTS', e)
		nfts = { nfts: [], message: 'Error Occurred Fetching NFTs', count: 0 }
	}

	try {
		const retrievedcollection = await getCollectionAttestation({
			collectionAttestationIPFSCID: params.cid,
		})
		if (retrievedcollection) {
			// If revoked, redirect to provenance page
			if (retrievedcollection.isRevoked) {
				return redirect(`/metadataProfile/collection/${params.cid}`, {
					status: 302,
				})
			}
			collection = retrievedcollection
			// Add Dynamic Avatar and Username
			if (retrievedcollection.artistName) {
				artist = {
					username: retrievedcollection.artistName,
					userAvatar: retrievedcollection.artistAvatar || undefined,
					userId: retrievedcollection.artistUserId || undefined,
				}
			} else if (
				retrievedcollection.artistUserId ||
				retrievedcollection.userId
			) {
				const artistUserId =
					retrievedcollection.artistUserId || retrievedcollection.userId
				try {
					const userAccount = await getUser(artistUserId)
					if (userAccount) {
						const dynamicUser = userAccount
						artist = {
							username: dynamicUser.username,
							userAvatar:
								dynamicUser.metadata && dynamicUser.metadata.avatar
									? dynamicUser.metadata.avatar
									: null,
							userId: artistUserId,
						}
					}
				} catch (e) {
					console.error('Error adding user data from db', e)
					try {
						const dynamicUser = await getDynamicUser({
							userId: artistUserId,
						})
						if (dynamicUser && dynamicUser.username) {
							artist = {
								username: dynamicUser.username,
								userAvatar:
									dynamicUser.metadata && dynamicUser.metadata.avatar
										? dynamicUser.metadata.avatar
										: undefined,
								userId: artistUserId,
							}
						}
					} catch (e) {
						console.error('Error adding dynamic data', e)
					}
				}
			}
			try {
				// @ts-ignore
				const collectionNFTProps =
					collection.references?.entry?.nft &&
					collection.template.slug === 'collection'
						? collection.references.entry.nft
							? mapReferenceEntriesToFormData(collection.references.entry.nft)
							: []
						: []
				if (collectionNFTProps && collectionNFTProps.length > 0) {
					collection.properties.nfts = collectionNFTProps
					const countAndChainResponse = await mapNFTsToCollection(collection)
					nftCount = countAndChainResponse.nftCount
					chains = countAndChainResponse.chains
				}
			} catch (e) {
				console.error('ERROR UPDATING PENDING SIGN NFT PROPERTIES', e)
			}
		} else {
			console.error('COLLECTION ATTESTATION NOT FOUND', {
				cid: params.cid,
			})
			throw new Response(
				`Error retrieving attestation for collection with cid or slug: ${params.cid}`,
				{
					status: 500,
				},
			)
		}
	} catch (e) {
		console.error('ERROR LOADING ATTESTATION FOR COLLECTION', {
			e,
			cid: params.cid,
		})
		throw new Response('Error retrieving attestation for collection!', {
			status: 500,
		})
	}

	return defer({
		cid: params.cid,
		artist,
		collection,
		collectionLore,
		nfts,
		nftCount,
		chains,
		origin,
		atomicLoreUrl: url.origin,
		signModalParams,
	})
}

export default function collectionPage() {
	const {
		cid,
		artist,
		collection,
		collectionLore,
		nfts,
		nftCount,
		chains,
		origin,
		atomicLoreUrl,
	} = useLoaderData<typeof loader>()

	const [
		clientCollectionAttestationsPromise,
		setClientCollectionAttestationsPromise,
	] = useState(collectionLore)
	const [isLoading, setIsLoading] = useState(false)
	const handleSigning = async () => {
		await loadAttestations()
	}

	const loadAttestations = async () => {
		if (isLoading) return
		setIsLoading(true)
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const response = await fetch(
				`/collectionAttestationAPI?cid=${cid}`,
				options,
			)
			const responseJson = response.json()
			setClientCollectionAttestationsPromise(responseJson)
		} catch (error) {
			console.error('ERROR LOADING NEW ATTESTATIONS', error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<CollectionProfile
			cid={cid}
			artist={artist}
			collection={collection}
			collectionLore={clientCollectionAttestationsPromise}
			nfts={nfts}
			nftCount={nftCount}
			chains={chains}
			origin={origin}
			atomicLoreUrl={atomicLoreUrl}
			handleRevalidation={handleSigning}
		/>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	const navigate = useNavigate()
	captureRemixErrorBoundaryError(error)

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
				<TypographyParagraph size="medium">or</TypographyParagraph>
				<Button onClick={() => navigate('.', { replace: true })}>
					Reload Page
				</Button>
			</Section>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
				<TypographyParagraph size="medium">or</TypographyParagraph>
				<Button onClick={() => navigate('.', { replace: true })}>
					Reload Page
				</Button>
			</Section>
		)
	} else {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading this Attestation
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
				<TypographyParagraph size="medium">or</TypographyParagraph>
				<Button onClick={() => navigate('.', { replace: true })}>
					Reload Page
				</Button>
			</Section>
		)
	}
}
